import { useState, useCallback, useMemo, useEffect } from 'react';
import { ExtendedClientFormData } from '../types';
import { ClientApproval } from '../../../types/client';
import { clientMasterApi, clientApprovalAPI } from '../../../services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { isNumericField, isBooleanField, areValuesEqual } from '../utils/fieldUtils';
import { isValidEmailList, isValidPhoneNumber } from '@/utils/validators';


interface UseClientFormProps {
    clientId?: number | null;
    approvalId?: string;
    clientType: 'PMS' | 'AIF';
    requiredFields: string[];
}

export const useClientForm = ({ clientId, approvalId, clientType, requiredFields: initialRequiredFields }: UseClientFormProps) => {
    const navigate = useNavigate();
    const [requiredFields, setRequiredFields] = useState<string[]>(initialRequiredFields || []);
    const [formData, setFormData] = useState<ExtendedClientFormData>({
        id: 0,
        client_code: '',
        first_name: '',
        middle_name: '',
        last_name: '',
        email: '',
        contact_no: '',
        pan_card: '',
        address: '',
        city: '',
        state: '',
        country_id: 0,
        fa_account_no: '',
        bank_name: '',
        bank_acc_no: '',
        bank_micr: '',
        bank_ifsc: '',
        bank_branch_address: '',
        savings_account_no: '',
        pis_account_no: '',
        is_nri: false,
        inception_date: '',
        agreement_date: '',
        maturity_date: '',
        performance_reporting_date: '',
        scheme: 0,
        category: 0,
        group: 0,
        client_class: 0,
        distributor: 0,
        restricted_companies: [],
        distributor_share: '',
        distributor_rm_name: '',
        relationship_manager_email: '',
        risk_status: null,
        block_amount: 0,
        stop_purchase: false,
        redemption_only: false,
        clearing_cash_enable: false,
        cash_threshold_limit: 10000, // Business default for new clients — visible in the Cash Threshold Limit field on creation
        is_derivative_consent: false,
        is_tax_applicable: true,
        occupation: '',
        notes: '',
        custody_code: '',
        custody_dp: '',
        custody_account_id: '',
        mode_of_holding: '',
        nominee_name: '',
        nominee_relationship: '',
        nominee_2_name: '',
        nominee_2_relationship: '',
        guardian_name: '',
        contact_person_name: '',
        contact_person_email: '',
        contact_person_contact_no: '',
        ubo_name: '',
        joint_holder_1_name: '',
        joint_holder_1_pan: '',
        joint_holder_2_name: '',
        joint_holder_2_pan: '',
        is_active: true,
        client_type: clientType
    } as ExtendedClientFormData);

    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    // Starts true when a client/approval will be fetched on mount, so the form
    // shows the spinner instead of blank fields until that fetch lands.
    const [dataLoading, setDataLoading] = useState(() => !!(approvalId || clientId)); // Managed locally for fetch calls
    const [approvalDetails, setApprovalDetails] = useState<ClientApproval | null>(null);
    const [originalClientData, setOriginalClientData] = useState<any>(null);
    const [changedFields, setChangedFields] = useState<Record<string, boolean>>({});
    const [selectedClientType, setSelectedClientType] = useState<'PMS' | 'AIF'>(clientType);

    const populateFormWithClient = useCallback((data: any) => {
        if (!data) return;

        setSelectedClientType(data.client_type || 'PMS');

        const getId = (val: any): number => {
            if (!val) return 0;
            if (typeof val === 'object' && 'id' in val) return Number(val.id);
            return Number(val) || 0;
        };

        // Normalize restricted companies - ensure it's an array of numbers
        const normalizeRestrictedCompanies = (companies: any): number[] => {
            if (!companies) return [];
            if (!Array.isArray(companies)) return [];

            return companies.map((c: any) => {
                if (typeof c === 'object' && c !== null) {
                    return Number(c.id || c.company_id || 0);
                }
                return Number(c);
            }).filter(id => id > 0); // Remove any invalid IDs
        };

        setFormData((prev: any) => ({
            ...prev,
            id: data.id || 0,
            client_code: data.client_code || '',
            client_type: data.client_type || 'PMS',
            first_name: data.first_name || '',
            middle_name: data.middle_name || '',
            last_name: data.last_name || '',
            email: data.email || '',
            contact_no: data.contact_no || '',
            pan_card: data.pan_card || '',
            address: data.address || '',
            city: data.city || '',
            state: data.state || '',
            country_id: getId(data.country_id || data.country),
            fa_account_no: data.fa_account_no || '',
            bank_name: data.bank_name || '',
            bank_acc_no: data.bank_acc_no || '',
            bank_micr: data.bank_micr || '',
            bank_ifsc: data.bank_ifsc || '',
            bank_branch_address: data.bank_branch_address || '',
            savings_account_no: data.savings_account_no || '',
            pis_account_no: data.pis_account_no || '',
            is_nri: !!data.is_nri,
            is_active: data.is_active ?? true,
            inception_date: data.inception_date || '',
            agreement_date: data.agreement_date || '',
            maturity_date: data.maturity_date || '',
            performance_reporting_date: data.performance_reporting_date || '',
            scheme: getId(data.scheme),
            category: getId(data.category),
            group: getId(data.group),
            client_class: data.client_type === 'AIF' ? getId(data.client_class) : 0,
            distributor: getId(data.distributor),
            restricted_companies: normalizeRestrictedCompanies(data.restricted_companies),
            distributor_share: data.distributor_share || '',
            distributor_rm_name: data.distributor_rm_name || '',
            relationship_manager_email: data.relationship_manager_email || '',
            risk_status: data.risk_status || null,
            block_amount: data.block_amount ?? 0,
            stop_purchase: !!data.stop_purchase,
            redemption_only: !!data.redemption_only,
            clearing_cash_enable: !!data.clearing_cash_enable,
            cash_threshold_limit: data.cash_threshold_limit ?? 0,
            is_derivative_consent: !!data.is_derivative_consent,
            is_tax_applicable: !!data.is_tax_applicable,
            occupation: data.occupation || '',
            notes: data.notes || '',
            custody_code: data.custody_code || '',
            custody_dp: data.custody_dp || '',
            custody_account_id: data.custody_account_id || data.custody_account || '',
            mode_of_holding: data.mode_of_holding || '',
            nominee_name: data.nominee_name || '',
            nominee_relationship: data.nominee_relationship || '',
            nominee_2_name: data.nominee_2_name || '',
            nominee_2_relationship: data.nominee_2_relationship || '',
            guardian_name: data.guardian_name || '',
            contact_person_name: data.contact_person_name || '',
            contact_person_email: data.contact_person_email || '',
            contact_person_contact_no: data.contact_person_contact_no || '',
            ubo_name: data.ubo_name || '',
            joint_holder_1_name: data.joint_holder_1_name || '',
            joint_holder_1_pan: data.joint_holder_1_pan || '',
            joint_holder_2_name: data.joint_holder_2_name || '',
            joint_holder_2_pan: data.joint_holder_2_pan || '',
        }));

        // setOriginalClientData(data); // <-- REMOVED: Do not overwrite original data here
    }, []);
    const fetchClient = useCallback(async (id: number, signal?: AbortSignal) => {
        try {
            setDataLoading(true);
            const data = await clientMasterApi.getClient(id, { signal });
            setOriginalClientData(data); // <-- ADDED: Explicitly set original data for normal edit mode
            populateFormWithClient(data);
        } catch (err: any) {
            if (err && err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
                toast.error('Failed to fetch client');
            }
        } finally {
            if (!signal?.aborted) {
                setDataLoading(false);
            }
        }
    }, [populateFormWithClient]);

    const fetchApproval = useCallback(async (approvalIdNum: number, signal?: AbortSignal) => {
        try {
            setDataLoading(true);
            const approvalData = await clientApprovalAPI.getApproval(approvalIdNum); // Assuming getApproval doesn't take config yet, but it's less frequent
            if (signal?.aborted) return;
            setApprovalDetails(approvalData);

            const snapshot = approvalData.data || {};
            const clientSnapshot = snapshot.clientmaster || snapshot.client || {}; // This is the ORIGINAL client data before changes
            const proposedClientData = approvalData.object_details || {}; // This is the PROPOSED client data with changes

            setOriginalClientData(clientSnapshot); // Set original data for comparison
            // Use Proposed Data as the form source of truth
            populateFormWithClient(proposedClientData);

            // Detect changed fields for highlighting
            const changed: Record<string, boolean> = {};
            Object.keys(clientSnapshot).forEach(key => {
                const snap = clientSnapshot as any;
                const proposed = proposedClientData as any;
                if (!areValuesEqual(key as any, proposed[key], snap[key])) {
                    changed[key] = true;
                }
            });
            setChangedFields(changed);

            // Merge nested fee structures from flat snapshot lists
            if (snapshot.clientfeecomponent && snapshot.performancefeestructure) {
                snapshot.clientfeecomponent = snapshot.clientfeecomponent.map((fc: any) => ({
                    ...fc,
                    fee_structures: snapshot.performancefeestructure.filter((fs: any) => fs.fee_component === fc.id)
                }));
            }

        } catch (err: any) {
            if (err && err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
                toast.error('Failed to fetch approval');
            }
        } finally {
            if (!signal?.aborted) {
                setDataLoading(false);
            }
        }
    }, [populateFormWithClient]);

    // Empty deps are intentional: this closes only over stable setState setters.
    // Do NOT add validationErrors (or any state) — it would change on every keystroke,
    // invalidating downstream useCallbacks like handleTradingSettingsChange.
    const handleInputChange = useCallback((field: keyof ExtendedClientFormData, value: any) => {
        let finalValue = value;

        // Auto-uppercase specific fields
        if (typeof value === 'string') {
            if (['pan_card', 'bank_ifsc', 'client_code', 'custody_code'].includes(field as string)) {
                finalValue = value.toUpperCase();
            }
        }

        setFormData((prev) => ({ ...prev, [field]: finalValue }));

        setValidationErrors(prev => {
            if (!prev[field as string]) return prev;
            const updated = { ...prev };
            delete updated[field as string];
            return updated;
        });
    }, []);

    const validateForm = useCallback(() => {
        const errors: Record<string, string> = {};
        requiredFields.forEach((field) => {
            const value = (formData as any)[field];
            if (!value || (Array.isArray(value) && value.length === 0)) {
                errors[field] = `${field.replace('_', ' ')} is required`;
            }
        });

        // Email Validation — one address, or several separated by commas
        const invalidEmailMessage = 'Enter a valid email address, or several separated by commas';
        if (formData.email && !isValidEmailList(formData.email)) {
            errors.email = invalidEmailMessage;
        }
        if (formData.relationship_manager_email && !isValidEmailList(formData.relationship_manager_email)) {
            errors.relationship_manager_email = invalidEmailMessage;
        }
        if (formData.contact_person_email && !isValidEmailList(formData.contact_person_email)) {
            errors.contact_person_email = invalidEmailMessage;
        }

        // Contact Number Validation (Global & Indian)
        const invalidContactMessage = 'Invalid contact number. Use 10 digits (India) or intl format (e.g., +14155552671)';
        if (formData.contact_no && !isValidPhoneNumber(formData.contact_no)) {
            errors.contact_no = invalidContactMessage;
        }
        if (formData.contact_person_contact_no && !isValidPhoneNumber(formData.contact_person_contact_no)) {
            errors.contact_person_contact_no = invalidContactMessage;
        }

        // Date Logic Validation
        if (formData.inception_date && formData.maturity_date) {
            if (new Date(formData.maturity_date) <= new Date(formData.inception_date)) {
                errors.maturity_date = 'Maturity date must be after Inception date';
            }
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    }, [formData, requiredFields]);

    return {
        formData,
        setFormData,
        validationErrors,
        setValidationErrors,
        loading,
        setLoading,
        dataLoading, // Exporting this loading state
        approvalDetails,
        originalClientData,
        changedFields,
        selectedClientType,
        setSelectedClientType,
        handleInputChange,
        validateForm,
        fetchClient,
        fetchApproval,
        setRequiredFields,
    };
};
